import React from 'react';
import { Building2, MapPin, ArrowRight } from 'lucide-react';

// Four hand-picked hotel search URLs on Booking.com. The CJ Deep Link
// Automation script (embedded in public/index.html) auto-monetises any
// booking.com outbound at click-time — no per-URL affiliate tag needed
// here. Every link uses `rel="sponsored"` for Google link-typing.
const HOTELS = [
  {
    name: 'Glynhill Hotel & Leisure Club',
    tagline: 'Paisley · 4★ · pool + spa',
    location: 'Renfrew Road, Paisley',
    url: 'https://www.booking.com/hotel/gb/glynhill.html',
    accent: 'from-slate-800 to-slate-950',
  },
  {
    name: 'Holiday Inn Glasgow Airport',
    tagline: 'Paisley · 4★ · 10 min from town',
    location: 'Abbotsinch, Paisley',
    url: 'https://www.booking.com/hotel/gb/holiday-inn-glasgow-airport.html',
    accent: 'from-slate-800 to-slate-950',
  },
  {
    name: 'Ashtree House Hotel',
    tagline: 'Paisley · Boutique · walk to Abbey',
    location: 'Orr Square, Paisley',
    url: 'https://www.booking.com/hotel/gb/ashtree-house.html',
    accent: 'from-slate-800 to-slate-950',
  },
  {
    name: 'All hotels in Paisley',
    tagline: 'Browse every property near town',
    location: 'Paisley area',
    url: 'https://www.booking.com/searchresults.html?ss=Paisley%2C+United+Kingdom',
    accent: 'from-cyan-800 to-slate-900',
    isPrimary: true,
  },
];

const trackClick = (hotel) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'affiliate_click', {
        affiliate: 'booking_com_cj',
        placement: 'homepage_places_to_stay',
        hotel: hotel.name,
      });
    }
  } catch (_e) { /* silent */ }
};

/**
 * Homepage tile: "Places to Stay in Paisley".
 *
 * Four hotel cards linking out to Booking.com. Monetisation happens via
 * the CJ Deep Link Automation script embedded globally, so we don't have
 * to bake an aid or PID into each URL. `rel="sponsored"` keeps us on the
 * right side of Google's link-typing rules; a small strapline under the
 * grid is the ASA-required affiliate disclosure.
 */
const PlacesToStayTile = () => (
  <section
    className="max-w-6xl mx-auto px-4 py-14"
    aria-label="Places to stay in Paisley — Booking.com affiliate"
    data-testid="places-to-stay-tile"
  >
    <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
      <div>
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700 mb-2">
          <Building2 size={13} /> Places to stay
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
          Somewhere to stay in Paisley
        </h2>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Handpicked hotels within a few minutes of Paisley Abbey and Glasgow Airport — best-price guaranteed via Booking.com.
        </p>
      </div>
      <a
        href="https://www.booking.com/searchresults.html?ss=Paisley%2C+United+Kingdom"
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={() => trackClick({ name: 'view_all' })}
        data-testid="places-to-stay-view-all"
        className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors"
      >
        See all Paisley hotels <ArrowRight size={14} />
      </a>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="places-to-stay-grid">
      {HOTELS.map((h) => (
        <a
          key={h.url}
          href={h.url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackClick(h)}
          data-testid={`places-to-stay-card-${h.name.replace(/\s+/g, '-').toLowerCase()}`}
          className={
            'group relative overflow-hidden rounded-2xl border p-5 aspect-[4/5] flex flex-col justify-between text-white shadow-md hover:shadow-xl transition-all bg-gradient-to-br ' +
            h.accent +
            (h.isPrimary ? ' border-cyan-400' : ' border-slate-700')
          }
        >
          <div className="flex items-start justify-between">
            <span className={
              'text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ' +
              (h.isPrimary ? 'bg-cyan-400 text-slate-900' : 'bg-white/10 text-white/80 backdrop-blur')
            }>
              {h.isPrimary ? 'View all' : 'Book direct'}
            </span>
            <Building2 size={16} className="text-white/40 group-hover:text-white/70 transition-colors" />
          </div>
          <div>
            <h3 className="text-lg font-bold leading-tight">{h.name}</h3>
            <p className="text-xs text-white/70 mt-1">{h.tagline}</p>
            <p className="text-[11px] text-white/50 mt-3 flex items-center gap-1">
              <MapPin size={11} /> {h.location}
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors">
              Check availability <ArrowRight size={13} />
            </span>
          </div>
        </a>
      ))}
    </div>

    <p className="text-[11px] text-slate-400 italic mt-4 text-center">
      Booking.com affiliate links — we may earn a small commission at no extra cost to you.
    </p>
  </section>
);

export default PlacesToStayTile;
